import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import {
  downloadAttachment,
  downloadDocumentAttachment,
  uploadAttachments,
} from '~/api/document';
import { EM_DASH } from '~/common/constants/unicode';
import { showToast } from '~/components/Shared/EcToast/showToast';
import FieldValuesMultiSelect from '~/components/Shared/FieldValuesMultiSelect/FieldValuesMultiSelect';
import FieldValuesSingleSelect from '~/components/Shared/FieldValuesSingleSelect/FieldValuesSingleSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { isWssField } from '~/features/document-handler/utils';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import * as toastTypes from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { getUniqueFieldValues } from '~/utils/document.utils';
import { downloadFile } from '~/utils/files';

import { black5, evisortBlue } from '../../../../assets/shared-styles/general';
import EcDatepicker from '../../../Shared/EcDatepicker/EcDatepicker';
import EcInput from '../../../Shared/EcInput/EcInput';
import EcMultipleSelectValue from '../../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcSelect from '../../../Shared/EcSelect/EcSelect';
import EditIcon from '../../../Shared/Icons/EditIcon';
import SearchIcon from '../../../Shared/Icons/SearchIcon';
import SubArrowIcon from '../../../Shared/Icons/SubArrowIcon';
import { editDocument } from '../../Document.data';
import { booleanOptions } from '../../DocumentOverviewPanel/helpers';
import styles from './InformationField.styles';
import { preprocessFieldValue } from './InformationField.utils';

class InformationField extends Component {
  constructor(props) {
    super(props);

    this.handleOnEditClick = this.handleOnEditClick.bind(this);
    this.handleOnCancelClick = this.handleOnCancelClick.bind(this);
    this.handleOnSaveClick = this.handleOnSaveClick.bind(this);
    this.state = {
      editMode: false,
      fieldValue: null,
      loading: false,
      isWssField:
        props.flags[FlagType.WSSDesignatedWorkflow] && isWssField(props.field),
    };
  }

  componentDidMount() {
    this.setInitialState();
  }

  setInitialState() {
    const { field } = this.props;
    if (field.type === DataFieldType.ARRAY_MULTIPLE) {
      this.setState({ fieldValue: getUniqueFieldValues(field.value) });
    } else {
      this.setState({ fieldValue: field.value });
    }
  }

  canEditField() {
    const { userVisibilityLevel, isTerminated } = this.props;
    return userVisibilityLevel === 'OPEN' && !isTerminated;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.editMode && nextProps.field.value !== prevState.fieldValue) {
      if (nextProps.field.type === DataFieldType.ARRAY_MULTIPLE) {
        return { fieldValue: getUniqueFieldValues(nextProps.field.value) };
      } else {
        return { fieldValue: nextProps.field.value };
      }
    } else {
      return null;
    }
  }

  downloadFileAttachment(fileObject) {
    const { documentId, flags, intl } = this.props;
    const downloadFn = flags[FlagType.DownloadDocumentAttachment]
      ? () => downloadDocumentAttachment(documentId, fileObject.value)
      : () => downloadAttachment(fileObject.value);

    downloadFn()
      .then((file) => {
        showToast(
          toastTypes.DOWNLOAD,
          intl.formatMessage(
            {
              id: 'InformationField.downloadSuccessMessage',
              defaultMessage: '{fileName} successfully downloaded.',
              description:
                'Success message when file is downloaded. Example: fileName = "Document Name"',
            },
            { fileName: fileObject.display_value },
          ),
        );
        downloadFile(file, fileObject.display_value);
      })
      .catch(() => {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.downloadFailureMessage',
            defaultMessage: 'Download attachment failed. Please try again.',
            description: 'Error message when file download fails',
          }),
        );
      });
  }

  handleFileAttachError() {
    const { intl } = this.props;
    showToast(
      toastTypes.ERROR,
      intl.formatMessage({
        id: 'InformationField.attachFileFailureMessage',
        defaultMessage: 'Attach file failed. Please try again.',
        description: 'Error message when file attachment fails',
      }),
    );
  }

  handleOnEditClick() {
    if (!this.canEditField()) return;
    this.setState({ editMode: !this.state.editMode });
  }

  handleOnCancelClick() {
    this.setInitialState();
    this.setState({ editMode: false });
  }

  async handleOnSaveClick() {
    if (!this.canEditField()) {
      this.setState({ editMode: false });
      return;
    }

    const { fieldValue } = this.state;
    const { documentId, field, refreshField, intl } = this.props;
    const processedFieldValue = preprocessFieldValue(fieldValue);
    const editedFieldData = [
      { id: field.id, label: field.name, value: processedFieldValue },
    ];

    this.setState({ loading: true, editMode: false });

    try {
      const res = await editDocument(documentId, editedFieldData);
      if (res[0].value === null) {
        res[0]['value'] = { value: null, display_value: '' };
      }
      await refreshField();
      this.setState({
        editMode: false,
        loading: false,
        fieldValue: res[0].value.value,
      });
      showToast(
        toastTypes.SUCCESS,
        intl.formatMessage({
          id: 'InformationField.updateSuccessMessage',
          defaultMessage: 'Document information field has been updated.',
          description: 'Success message when field is updated',
        }),
      );
    } catch (e) {
      this.setInitialState();
      this.setState({ editMode: false, loading: false });
      if (e.response && e.response.status === 400) {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.fieldLimitErrorMessage',
            defaultMessage:
              'Only 30 field values can be added or removed at a time. Please update and try again.',
            description: 'Error message when field value limit is exceeded',
          }),
        );
      } else {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.updateFailureMessage',
            defaultMessage:
              'An error occurred while updating information field.',
            description: 'General error message when field update fails',
          }),
        );
      }
    }
  }

  renderBooleanField(field) {
    const { classes } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const presentValue = fieldValue
        ? { label: fieldValue.display_value, value: fieldValue.value }
        : null;

      return (
        <div className={classes.editItemContainer}>
          <EcSelect
            aria-label={field.name}
            defaultValue={presentValue}
            options={booleanOptions}
            onChange={(newValue) =>
              this.setState({
                fieldValue: {
                  display_value: newValue.label,
                  value: newValue.value,
                },
              })
            }
            isDisabled={!field.is_editable}
            isSearchable={false}
          />
        </div>
      );
    }

    return fieldValue ? fieldValue.display_value : EM_DASH;
  }

  renderNumberField(field) {
    const { classes, intl } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const presentValue = fieldValue ? fieldValue.value : '';

      return (
        <div className={classes.editItemContainer}>
          <EcInput
            ariaLabel={field.name}
            input={{
              value: presentValue,
              onChange: (event) => {
                const newState =
                  event.target.value === '' ? '' : Number(event.target.value);
                this.setState({
                  fieldValue: { display_value: newState, value: newState },
                });
              },
            }}
            placeholder={intl.formatMessage({
              id: 'InformationField.enterAmountPlaceholder',
              defaultMessage: 'enter amount',
              description: 'Placeholder text for number input field',
            })}
            type="number"
            width="100%"
            isDisabled={!field.is_editable}
          />
        </div>
      );
    }

    return fieldValue ? fieldValue.display_value : EM_DASH;
  }

  renderDateField(field) {
    const { classes, formatDate } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const { focused } = this.state;
      const presentValue = fieldValue ? fieldValue.value : '';

      return (
        <div className={classes.editItemContainer}>
          <EcDatepicker
            title={field.name}
            date={presentValue ? parseDate(presentValue, true) : null}
            focused={focused}
            onDateChange={(date) => {
              const newDate = date ? formatDate(date, 'iso') : null;
              this.setState({
                fieldValue: { display_value: newDate, value: newDate },
              });
            }}
            isClearable={field.is_nullable}
            disabled={!field.is_editable}
          />
        </div>
      );
    }
    return !fieldValue || fieldValue.display_value === ''
      ? EM_DASH
      : formatDate(parseDate(fieldValue.display_value, true), 'long');
  }

  renderStringField(field) {
    const { classes, intl } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const presentValue = fieldValue ? fieldValue.value : '';

      return (
        <div className={classes.editItemContainer}>
          <EcInput
            ariaLabel={field.name}
            input={{
              value: presentValue,
              onChange: (event) =>
                this.setState({
                  fieldValue: {
                    display_value: event.target.value,
                    value: event.target.value,
                  },
                }),
            }}
            placeholder={intl.formatMessage(
              {
                id: 'InformationField.enterFieldPlaceholder',
                defaultMessage: 'Enter {fieldName}',
                description:
                  'Placeholder text for string input field. Example: fieldName = "Contract Title"',
              },
              { fieldName: field.name },
            )}
            width="100%"
            isDisabled={!field.is_editable}
          />
        </div>
      );
    }

    return !fieldValue || fieldValue.display_value === ''
      ? EM_DASH
      : fieldValue.display_value;
  }

  renderSingleArrayField(field) {
    const { classes } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const presentValue = fieldValue
        ? { label: fieldValue.display_value, value: fieldValue.value }
        : null;

      return (
        <div className={classes.editItemContainer}>
          <FieldValuesSingleSelect
            aria-label={field.name}
            defaultValue={presentValue}
            onChange={(newValue) => {
              const newState = newValue ? newValue.value : newValue;
              this.setState({
                fieldValue: { display_value: newState, value: newState },
              });
            }}
            isDisabled={!field.is_editable}
            isClearable={field.is_nullable}
            isCreatable={field.allow_new_options}
            fieldId={field.id}
            fieldValues={field.values}
          />
        </div>
      );
    }

    return fieldValue ? fieldValue.display_value : EM_DASH;
  }

  renderMultipleValues({ is_editable }) {
    const { classes, intl } = this.props;
    const { fieldValue } = this.state;

    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {fieldValue.length ? (
            fieldValue.map((value, index) => (
              <EcMultipleSelectValue
                label={value.display_value}
                key={index}
                marginBottom="8px"
                closeHandler={() => {
                  const newState = this.state.fieldValue.filter(
                    (item) => item.value !== value.value,
                  );
                  this.setState({ fieldValue: newState });
                }}
                isDisabled={!is_editable}
              />
            ))
          ) : (
            <span className={classes.multipleValuesEmpty}>
              {intl.formatMessage({
                id: 'InformationField.noValuesSelectedMessage',
                defaultMessage: 'No values selected...',
                description:
                  'Message when no values are selected for multiple select field',
              })}
            </span>
          )}
        </div>
      </div>
    );
  }

  renderMultipleArrayField(field) {
    const { classes, flags, intl } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      const presentValue =
        fieldValue && fieldValue.length
          ? fieldValue.map((val) => ({
              label: val.display_value,
              value: val.value,
            }))
          : [];
      const disableSelectAll =
        flags[FlagType.FieldValueMigrationDeferredFeaturesBypass];
      return (
        <div className={classes.editItemContainerMultiple}>
          <FieldValuesMultiSelect
            aria-label={field.name}
            value={presentValue}
            onChange={(newItems) => {
              const newState = newItems.map((item) => ({
                display_value: item.label,
                value: item.value,
              }));
              this.setState({ fieldValue: newState });
            }}
            width="100%"
            placeholder={intl.formatMessage(
              {
                id: 'InformationField.searchFieldPlaceholder',
                defaultMessage: 'Search for {fieldName}...',
                description:
                  'Placeholder text for multi-select search field. Example: fieldName = "Contract Type"',
              },
              { fieldName: field.name },
            )}
            noOptionsMessage={intl.formatMessage(
              {
                id: 'InformationField.noOptionsMessage',
                defaultMessage: 'No {fieldName} found',
                description:
                  'Message when no options are found in search. Example: fieldName = "Contract Type"',
              },
              { fieldName: field.name },
            )}
            isDisabled={!field.is_editable}
            isCreatable={field.allow_new_options}
            useMinInputLength={['Counterparties', 'Internal Parties'].includes(
              field.class_name,
            )}
            allowSelectAll
            disableSelectAll={disableSelectAll}
            fieldId={field.id}
            fieldValues={field.values}
          />
          {this.renderMultipleValues(field)}
        </div>
      );
    }

    return fieldValue && fieldValue.length
      ? fieldValue.map((val) => val.display_value).join(' • ')
      : EM_DASH;
  }

  renderTextAreaField(field) {
    const { classes } = this.props;
    const { fieldValue } = this.state;

    return (
      <div className={classes.editItemContainer}>
        <textarea
          aria-label={field.name}
          value={fieldValue ? fieldValue.value : ''}
          rows="7"
          disabled={!this.state.editMode}
          className={classes.textArea}
          onChange={(event) => {
            this.setState({
              fieldValue: {
                display_value: event.target.value,
                value: event.target.value,
              },
            });
          }}
        />
      </div>
    );
  }

  renderAttachmentField(field) {
    const { intl } = this.props;

    const handleFileDataChangeHelper = (newFiles) => {
      if (!newFiles || !newFiles.length) {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.couldNotAttachFileMessage',
            defaultMessage: 'Could not attach file.',
            description: 'Error message when file could not be attached',
          }),
        );
        return;
      }
      const newItems = newFiles.map((fileData) => ({
        display_value: fileData.fileName + fileData.fileType,
        value: fileData.id,
      }));
      this.setState({ fieldValue: [...fieldValue, ...newItems] });
      showToast(
        toastTypes.SUCCESS,
        intl.formatMessage({
          id: 'InformationField.attachFileSuccessMessage',
          defaultMessage: 'Attach file succeeded.',
          description: 'Success message when file is attached',
        }),
      );
    };

    const { classes } = this.props;
    const { fieldValue } = this.state;

    if (this.state.editMode) {
      return (
        <div className={classes.editItemContainerMultiple}>
          <FileInput
            acceptedFiles="*"
            enableUpload
            enableDropzone
            enableMultiple
            layout="condensed"
            onChange={(files) =>
              uploadAttachments(
                files,
                handleFileDataChangeHelper,
                this.handleFileAttachError,
              )
            }
          />
          {this.renderMultipleValues(field)}
        </div>
      );
    }

    if (fieldValue?.length) {
      // TODO: Refactor to avoid duplicating this code with DocumentOverviewPanel.js
      return (
        <FlexLayout flexDirection="column" space={5}>
          {fieldValue.map((fileObject) => {
            const file = new File([], fileObject.display_value);
            return (
              <Box onClick={() => this.downloadFileAttachment(fileObject)}>
                <FileLabel shouldTruncate file={file} />
              </Box>
            );
          })}
        </FlexLayout>
      );
    } else {
      return EM_DASH;
    }
  }

  renderFieldValue(field) {
    let returnValue;

    switch (field.type) {
      case DataFieldType.BOOLEAN:
        returnValue = this.renderBooleanField(field);
        break;
      case DataFieldType.DATE:
        returnValue = this.renderDateField(field);
        break;
      case DataFieldType.NUMBER:
        returnValue = this.renderNumberField(field);
        break;
      case DataFieldType.REFERENCE:
      case DataFieldType.STRING:
        returnValue = this.renderStringField(field);
        break;
      case DataFieldType.ARRAY_SINGLE:
        returnValue = this.renderSingleArrayField(field);
        break;
      case DataFieldType.ARRAY_MULTIPLE:
        returnValue = this.renderMultipleArrayField(field);
        break;
      case DataFieldType.TEXT_AREA:
        returnValue = this.renderTextAreaField(field);
        break;
      case DataFieldType.ATTACHMENT:
        returnValue = this.renderAttachmentField(field);
        break;
      default:
        returnValue = EM_DASH;
        break;
    }

    return returnValue;
  }

  renderFieldHover() {
    const { classes, isFocused, isHovered, documentId, field, intl } =
      this.props;
    const { editMode, isWssField } = this.state;
    const canEdit = this.canEditField();

    const shouldShowEdit =
      (isFocused || isHovered) && !field.is_flagged && canEdit && !isWssField;

    if (editMode || !shouldShowEdit) return null;
    return (
      <div
        tabIndex="-1"
        id={`document_contentsPanel_editKeyInformation?document=${documentId}&keyInformation=${field.id}`}
        className={classes.fieldActionIcons}
      >
        <span className={classes.horizontalSeparator} />
        <button
          onClick={this.handleOnEditClick}
          title={intl.formatMessage(
            {
              id: 'InformationField.editFieldTooltip',
              defaultMessage: 'Edit {fieldName} field',
              description:
                'Tooltip for edit field button. Example: fieldName = "Document Name"',
            },
            { fieldName: field.name },
          )}
        >
          <EditIcon />
        </button>
      </div>
    );
  }

  renderFieldFooter() {
    const { classes, field, intl } = this.props;
    const { editMode } = this.state;
    const canEdit = this.canEditField();

    if (!canEdit) return null;

    if (field.is_flagged && !editMode) {
      return (
        <div className={classes.fieldFooter}>
          <Button
            icon="check"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.acceptButton',
              defaultMessage: 'Accept',
              description: 'Button text to accept field changes',
            })}
            onClick={this.handleOnSaveClick}
          />
          <Button
            icon="pencil"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.editButton',
              defaultMessage: 'Edit',
              description: 'Button text to edit field',
            })}
            onClick={this.handleOnEditClick}
          />
        </div>
      );
    } else if (editMode) {
      return (
        <div className={classes.fieldFooter}>
          <Button
            icon="check"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.saveButton',
              defaultMessage: 'Save',
              description: 'Button text to save field changes',
            })}
            onClick={this.handleOnSaveClick}
          />
          <Button
            text={intl.formatMessage({
              id: 'InformationField.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Button text to cancel field changes',
            })}
            onClick={this.handleOnCancelClick}
          />
        </div>
      );
    }

    return null;
  }

  renderLoadingSpinner() {
    const { classes, field } = this.props;

    return (
      <div className={classes.loadingContainer}>
        <LoadingSpinner label={`documents spin button ${field.name}`} />
      </div>
    );
  }

  sectionFieldClass() {
    const { classes } = this.props;
    const { loading, editMode } = this.state;

    if (loading && editMode) {
      return classes.sectionFieldEditModeLoading;
    } else if (loading) {
      return classes.sectionFieldLoading;
    }

    return editMode ? classes.sectionFieldEditMode : classes.sectionField;
  }

  shouldHaveFindButton = () => {
    const { field, isPdfHighlighter, showOCR } = this.props;
    if (!showOCR) return false;

    return isPdfHighlighter
      ? field.coordinates.length > 0
      : field.html_tokens.length > 0;
  };

  render() {
    const { classes, field, onFieldTokenClick, intl } = this.props;
    const { loading, isWssField } = this.state;

    const sectionFieldClass = this.sectionFieldClass();

    return (
      <div className={classes.sectionFieldWrapper} role="menuitem" tabIndex="0">
        <div className={sectionFieldClass}>
          <div className={classes.fieldTitle}>
            {field.name}
            {field.is_beta ? (
              <Tooltip
                id="betaTag"
                placement="left"
                tooltip={intl.formatMessage({
                  id: 'InformationField.betaTooltip',
                  defaultMessage:
                    "This is a newly released intelligent field which is currently in beta testing. If you have feedback about the outputs you're seeing, please send us a note at beta@evisort.com",
                  description: 'Tooltip text for beta field indicator',
                })}
              >
                <span
                  className={classes.betaTooltip}
                  data-tip
                  data-for="betaTag"
                >
                  {intl.formatMessage({
                    id: 'InformationField.betaLabel',
                    defaultMessage: 'BETA',
                    description: 'Beta field label',
                  })}
                </span>
              </Tooltip>
            ) : null}
            {field.help_text ? (
              <Icon
                size="s"
                icon="info"
                id={`helpTextTag-${field.id}`}
                tooltip={field.help_text}
                placement="left"
              />
            ) : null}
            {isWssField ? (
              <Icon
                icon="database"
                tooltip={intl.formatMessage({
                  id: 'InformationField.wssSourceTooltip',
                  defaultMessage: 'Source: Strategic Sourcing',
                  description: 'Tooltip for WSS field source indicator',
                })}
              />
            ) : null}
            {this.shouldHaveFindButton() ? (
              <button
                title={intl.formatMessage(
                  {
                    id: 'InformationField.findInDocumentTooltip',
                    defaultMessage:
                      'Find {fieldName} information in the document',
                    description:
                      'Tooltip for find in document button. Example: fieldName = "Document Name"',
                  },
                  { fieldName: field.name },
                )}
                onClick={() => onFieldTokenClick(field)}
                className={classes.fieldTokenIndicator}
              >
                <SearchIcon color={evisortBlue} size="18" opacity="1" />
              </button>
            ) : null}
          </div>
          <div className={classes.fieldValue}>
            {this.renderFieldValue(field)}
          </div>
          {this.renderFieldHover()}
          {this.renderFieldFooter()}
        </div>
        {loading ? this.renderLoadingSpinner() : null}
      </div>
    );
  }
}

export default injectSheet(styles)(
  withFlags(injectIntl(withFormatDate(InformationField))),
);
